import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import ReportDownloadDialog from './ReportDownloadDialog';
import { REPORT_SCOPE } from '../../api/companyReportApi';

/**
 * The download affordance, and the dialog it opens.
 *
 * Kept as one component rather than a button plus a separately-mounted dialog
 * so a caller adding a download only has to place one element — the dashboard
 * has four of these and every feature hub will have one.
 *
 * Two looks: `icon` for the plane headers, where it sits beside a heading and
 * must not compete with it, and `button` for a section page's action slot.
 */

const ICON_CLASS =
  'inline-flex items-center justify-center w-7 h-7 rounded-full text-shoorah-gray4 dark:text-shoorah-darkBg hover:bg-shoorah-normanGray dark:hover:bg-shoorah-darkBgTabColor focus:outline-none focus-visible:ring-2 focus-visible:ring-shoorah-active transition-colors';

const BUTTON_CLASS =
  'inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-shoorah-inputBorder dark:border-shoorah-darkBgTabColor text-sm font-medium text-shoorah-lightBlack dark:text-shoorah-darkBg hover:bg-shoorah-normanGray dark:hover:bg-shoorah-darkBgTabColor focus:outline-none focus-visible:ring-2 focus-visible:ring-shoorah-active transition-colors';

const DownloadReportButton = ({
  scope = REPORT_SCOPE.FEATURE,
  featureKey = null,
  featureLabel = null,
  plane = null,
  planeLabel = null,
  filters = {},
  variant = 'icon',
  label = 'Download report',
  onQueued,
  api,
  subject
}) => {
  const [open, setOpen] = useState(false);

  const what = featureLabel || planeLabel || 'dashboard';

  return (
    <>
      {variant === 'button' ? (
        <button type="button" className={BUTTON_CLASS} onClick={() => setOpen(true)}>
          <ArrowDownTrayIcon className="w-4 h-4" />
          {label}
        </button>
      ) : (
        <button
          type="button"
          className={ICON_CLASS}
          onClick={() => setOpen(true)}
          // The icon alone says nothing about what it downloads, and there are
          // four of them on the dashboard.
          aria-label={`Download ${what} report`}
          title={`Download ${what} report`}
        >
          <ArrowDownTrayIcon className="w-[18px] h-[18px]" />
        </button>
      )}

      <ReportDownloadDialog
        open={open}
        onClose={() => setOpen(false)}
        scope={scope}
        featureKey={featureKey}
        featureLabel={featureLabel}
        plane={plane}
        planeLabel={planeLabel}
        filters={filters}
        onQueued={onQueued}
        api={api}
        subject={subject}
      />
    </>
  );
};

DownloadReportButton.propTypes = {
  scope: PropTypes.oneOf(Object.values(REPORT_SCOPE)),
  featureKey: PropTypes.string,
  featureLabel: PropTypes.string,
  plane: PropTypes.string,
  planeLabel: PropTypes.string,
  filters: PropTypes.object,
  variant: PropTypes.oneOf(['icon', 'button']),
  label: PropTypes.string,
  onQueued: PropTypes.func,
  // Passed straight through; see ReportDownloadDialog.
  api: PropTypes.object,
  subject: PropTypes.object
};

export default DownloadReportButton;
